import throttle from 'lodash.throttle';
import g from '#core/global';
import load from '#core/load';


const outer_container_$ = '.mayor-slider';
const container_$ = '.mayor-slider__slider';
const items_$ = '.mayor-slider__slider-items';
const item_$ = '.mayor-slider__item';
const prev_$ = '.mayor-slider__nav-button--previous';
const next_$ = '.mayor-slider__nav-button--next';
const footer_$ = '.mayor-slider__footer';

let sliders = [];


const module = {
	container_$,
	items_$,
	item_$,
	prev_$,
	next_$,
	
	
	/**
	 * Initialize the mayor slider module
	 * Sets up sliders and event listeners
	 * @return {boolean} False if already initialized
	 */
	init() {
		if (load.ran('mayor-slider')) return false;
		
		load.content(() => {
			init_sliders();
		});
		
		load.all(() => {
			init_sliders();
			
			window.addEventListener(g.resize_e, throttle(() => {
				update_all_sliders();
			}, 150));
		});
	},
	
};

export default module;
module.init();


/**
 * Initialize all slider instances found in the DOM
 * Creates new MayorSlider instances for containers that haven't been initialized
 */
function init_sliders() {
	const $outer_containers = document.querySelectorAll(outer_container_$);
	
	$outer_containers.forEach($outer_container => {
		if ($outer_container.dataset.sliderInit) return;
		
		const slider = new MayorSlider($outer_container);
		sliders.push(slider);
		$outer_container.dataset.sliderInit = 'true';
	});
}


/**
 * Update dimensions and positions for all slider instances
 * Called on window resize to recalculate responsive behavior
 */
function update_all_sliders() {
	sliders.forEach(slider => {
		slider.updateDimensions();
		slider.updatePosition();
		slider.updateButtonStates();
	});
}


class MayorSlider {
	
	/**
	 * Create a new finite slider instance
	 * @param {HTMLElement} $outer_container
	 */
	constructor($outer_container) {
		this.$container = $outer_container;
		this.$footer = $outer_container.querySelector(footer_$);
		this.$items = $outer_container.querySelector(items_$);
		this.$itemElements = [...$outer_container.querySelectorAll(item_$)];
		this.$prevBtn = $outer_container.querySelector(prev_$);
		this.$nextBtn = $outer_container.querySelector(next_$);
		
		this.currentPosition = 0;
		this.totalItems = this.$itemElements.length;
		this.visibleItems = 1;
		this.isAnimating = false;
		this.isSliderActive = true;
		
		// Touch/swipe properties
		this.startX = 0;
		this.startY = 0;
		this.currentX = 0;
		this.isDragging = false;
		this.startTransform = 0;
		this.minSwipeDistance = 50;
		
		this.init();
	}
	
	
	/**
	 * Initialize the slider
	 */
	init() {
		this.updateDimensions();
		this.updatePosition();
		this.bindEvents();
		this.updateButtonStates();
	}
	
	
	/**
	 * Update slider position using CSS transform
	 */
	updatePosition() {
		if (!this.isSliderActive) {
			// Center the cards when slider is disabled
			this.$items.style.transform = '';
			this.$items.style.justifyContent = 'center';
			return;
		}
		
		this.$items.style.justifyContent = '';
		
		// Calculate transform based on current position and item width
		// Get the width of the first item to calculate offset
		if (this.$itemElements.length > 0) {
			const item_width = this.$itemElements[0].offsetWidth;
			const transform_px = this.currentPosition * item_width;
			this.$items.style.transform = `translateX(-${transform_px}px)`;
		}
	}
	
	
	/**
	 * Update slider dimensions and check if sliding should be active
	 */
	updateDimensions() {
		this.detectVisibleItems();
		
		// Disable slider if all items fit in the viewport
		this.isSliderActive = this.totalItems > this.visibleItems;
		
		// Show/hide navigation based on slider state
		if (this.$footer) {
			this.$footer.setAttribute('data-is-active', this.isSliderActive.toString());
		}
		
		// Reset position if it's beyond the new bounds
		const max_position = this.getMaxPosition();
		if (this.currentPosition > max_position) {
			this.currentPosition = max_position;
		}
	}
	
	
	/**
	 * Calculate maximum position (last valid slide position)
	 * @return {number} Maximum position index
	 */
	getMaxPosition() {
		if (!this.isSliderActive) return 0;
		return Math.max(0, this.totalItems - this.visibleItems);
	}
	
	
	/**
	 * Detect how many items are visible based on CSS breakpoints
	 * Matches the explicit widths defined in mayor-slider.scss
	 */
	detectVisibleItems() {
		// Match CSS breakpoints from mayor-slider.scss
		if (window.innerWidth >= 1292) { // b7: 25% width = 4 cards
			this.visibleItems = Math.min(4, this.totalItems);
		} else if (window.innerWidth >= 900) { // b5: 33.3333% width = 3 cards
			this.visibleItems = Math.min(3, this.totalItems);
		} else if (window.innerWidth >= 596) { // b3: 50% width = 2 cards
			this.visibleItems = Math.min(2, this.totalItems);
		} else { // Below b3: 80% width = 1 card visible
			this.visibleItems = 1;
		}
	}
	
	
	/**
	 * Bind event listeners for navigation and touch/swipe
	 */
	bindEvents() {
		// Navigation buttons
		if (this.$prevBtn) {
			this.$prevBtn.addEventListener('click', (e) => {
				e.preventDefault();
				this.prev();
			});
		}
		
		if (this.$nextBtn) {
			this.$nextBtn.addEventListener('click', (e) => {
				e.preventDefault();
				this.next();
			});
		}
		
		// Touch events for swipe
		this.$items.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
		this.$items.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
		this.$items.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
		
		// Mouse events for drag (desktop)
		this.$items.addEventListener('mousedown', this.handleMouseDown.bind(this));
		document.addEventListener('mousemove', this.handleMouseMove.bind(this));
		document.addEventListener('mouseup', this.handleMouseUp.bind(this));
		
		// Prevent text selection during drag
		this.$items.addEventListener('selectstart', (e) => {
			if (this.isDragging) e.preventDefault();
		});
	}
	
	
	/**
	 * Navigate to previous set of items
	 */
	prev() {
		if (!this.isSliderActive || this.isAnimating || this.currentPosition === 0) return;
		
		this.isAnimating = true;
		this.currentPosition = Math.max(0, this.currentPosition - this.visibleItems);
		this.updatePosition();
		this.updateButtonStates();
		this.waitForTransition();
	}
	
	
	/**
	 * Navigate to next set of items
	 */
	next() {
		if (!this.isSliderActive || this.isAnimating) return;
		
		const max_position = this.getMaxPosition();
		if (this.currentPosition >= max_position) return;
		
		this.isAnimating = true;
		this.currentPosition = Math.min(max_position, this.currentPosition + this.visibleItems);
		this.updatePosition();
		this.updateButtonStates();
		this.waitForTransition();
	}
	
	
	/**
	 * Wait for CSS transition to complete
	 */
	waitForTransition() {
		const handle_transition_end = (e) => {
			if (e.target === this.$items && e.propertyName === 'transform') {
				this.$items.removeEventListener('transitionend', handle_transition_end);
				this.isAnimating = false;
				this.updateButtonStates();
			}
		};
		
		this.$items.addEventListener('transitionend', handle_transition_end);
		
		// Fallback timeout
		setTimeout(() => {
			this.$items.removeEventListener('transitionend', handle_transition_end);
			this.isAnimating = false;
			this.updateButtonStates();
		}, 600);
	}
	
	
	/**
	 * Update button disabled states based on current position and slider state
	 */
	updateButtonStates() {
		if (!this.$prevBtn || !this.$nextBtn) return;
		
		if (!this.isSliderActive) {
			// Hide buttons when slider is inactive
			this.$prevBtn.disabled = true;
			this.$nextBtn.disabled = true;
			return;
		}
		
		// Disable pointer events during animation but keep visual state based on position
		if (this.isAnimating) {
			this.$prevBtn.style.pointerEvents = 'none';
			this.$nextBtn.style.pointerEvents = 'none';
		} else {
			this.$prevBtn.style.pointerEvents = '';
			this.$nextBtn.style.pointerEvents = '';
		}
		
		// Only disable based on position boundaries, not animation state
		this.$prevBtn.disabled = this.currentPosition === 0;
		this.$nextBtn.disabled = this.currentPosition >= this.getMaxPosition();
	}
	
	
	// Touch/swipe handling methods
	handleTouchStart(e) {
		if (!this.isSliderActive) return;
		this.startDrag(e.touches[0].clientX, e.touches[0].clientY);
	}
	
	handleTouchMove(e) {
		if (!this.isDragging) return;
		e.preventDefault();
		this.updateDrag(e.touches[0].clientX);
	}
	
	handleTouchEnd(e) {
		this.endDrag();
	}
	
	handleMouseDown(e) {
		if (!this.isSliderActive) return;
		e.preventDefault();
		this.startDrag(e.clientX, e.clientY);
	}
	
	handleMouseMove(e) {
		if (!this.isDragging) return;
		e.preventDefault();
		this.updateDrag(e.clientX);
	}
	
	handleMouseUp(e) {
		this.endDrag();
	}
	
	startDrag(x, y) {
		this.isDragging = true;
		this.startX = x;
		this.startY = y;
		this.currentX = x;
		this.startTransform = this.getTransformX();
		this.$items.style.transition = 'none';
	}
	
	updateDrag(x) {
		this.currentX = x;
		const delta_x = this.currentX - this.startX;
		const new_transform = this.startTransform + delta_x;
		this.$items.style.transform = `translateX(${new_transform}px)`;
	}
	
	endDrag() {
		if (!this.isDragging) return;
		
		this.isDragging = false;
		this.$items.style.transition = '';
		
		const delta_x = this.currentX - this.startX;
		const delta_y = Math.abs(this.startY - this.currentX);
		
		// Only trigger slide if horizontal swipe exceeds minimum distance
		if (Math.abs(delta_x) > this.minSwipeDistance && Math.abs(delta_x) > delta_y * 2) {
			if (delta_x > 0) {
				this.prev();
			} else {
				this.next();
			}
		} else {
			// Snap back to current position
			this.updatePosition();
		}
	}
	
	getTransformX() {
		const transform = window.getComputedStyle(this.$items).transform;
		if (transform === 'none') return 0;
		
		const matrix = transform.match(/matrix.*\((.+)\)/);
		if (!matrix) return 0;
		
		const values = matrix[1].split(', ');
		return parseFloat(values[4]) || 0;
	}
	
}
